import React, { useState } from "react";
import useStore from "../../../../../context/useStore";
import useInsuranceCompanies from "../../../../../hooks/useInsuranceCompanies/useInsuranceCompanies";
import useProducers from "../../../../../hooks/useProducers/useProducers";
import useProducts from "../../../../../hooks/useProducts/useProducts";
import useProductTypes from "../../../../../hooks/useProductTypes/useProductTypes";
import useRole from "../../../../../hooks/useRole/useRole";
import useClientFilters from "./useClientFilters";
import { CHAMPS_LIES_AU_CONTRAT, estSansFiche } from "./clientFilterRules";
import { SEARCH_SCOPES, typesClientPour } from "./clientPresets";

const parNom = (a, b) => (a.name || "").localeCompare(b.name || "", "fr");

// Liste de puces à choix multiple (compagnies, types de produit, produits),
// avec une recherche quand la liste est longue.
const ChoixMultiple = ({ id, label, options, valeurs, onToggle, disabled, avecRecherche, vide }) => {
  const [requete, setRequete] = useState("");
  const visibles = [...options]
    .filter((o) => !requete || (o.name || "").toLowerCase().includes(requete.toLowerCase()))
    .sort(parNom);

  return (
    <div className={"lf-field" + (disabled ? " lf-off" : "")}>
      <label id={`${id}-libelle`}>{label}</label>
      {avecRecherche && (
        <div className="lf-asg-search">
          <i className="fa fa-search" aria-hidden="true" />
          <input
            type="text"
            value={requete}
            placeholder="Rechercher…"
            aria-label={`Rechercher dans ${label}`}
            disabled={disabled}
            onChange={(e) => setRequete(e.target.value)}
          />
        </div>
      )}
      <div className="lf-people" role="group" aria-labelledby={`${id}-libelle`}>
        {visibles.length === 0 && <span className="lf-empty">{vide || "Aucun élément"}</span>}
        {visibles.map((o) => {
          const on = valeurs.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              className={"lf-person" + (on ? " lf-on" : "")}
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onToggle(o.id)}
            >
              {o.name}
              {on && <i className="fa fa-times lf-x-mini" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Tiroir avancé : identité (cinq champs texte), type de client, statut du
// compte, compagnies, types de produit, produits, société partenaire, options.
const ClientAdvancedFiltersPanel = ({ onClose, isLoading }) => {
  const [state] = useStore();
  const { ACCOUNT_STATUS } = state.constants.items;
  const { myRole } = useRole();
  const { productTypes } = useProductTypes();
  const { products } = useProducts();
  const { insuranceCompanies } = useInsuranceCompanies();
  const { producers } = useProducers();
  const { filtres, actions } = useClientFilters();
  const { updateFilter } = actions;

  const sansFiche = estSansFiche(filtres);
  const lieAuContrat = (champ) => sansFiche && CHAMPS_LIES_AU_CONTRAT.includes(champ);

  const basculer = (name, id) => {
    const courant = filtres[name] || [];
    updateFilter(name, courant.includes(id) ? courant.filter((v) => v !== id) : [...courant, id]);
  };

  // Les produits suivent les types cochés, comme avant la refonte.
  const produitsVisibles = filtres.typesProduits.length
    ? products.filter((p) => filtres.typesProduits.includes(p.productType))
    : products;

  const partenaires = [...producers].sort((a, b) => (a.company || "").localeCompare(b.company || "", "fr"));

  return (
    <div className="lf-panel">
      <div className="lf-panel-grid">
        <div className="lf-field lf-field-large">
          <label>Identité</label>
          <div className="lf-inputs">
            {SEARCH_SCOPES.map((s) => {
              const off = lieAuContrat(s.id);
              return (
                <div key={s.id} className={off ? "lf-off" : ""}>
                  <label htmlFor={`client-filtre-${s.id}`}>{s.label}</label>
                  <input
                    id={`client-filtre-${s.id}`}
                    type="text"
                    className="lf-input"
                    value={filtres[s.id]}
                    placeholder={s.placeholder}
                    disabled={off}
                    onChange={(e) =>
                      updateFilter(s.id, s.normaliser ? s.normaliser(e.target.value) : e.target.value)
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="lf-field">
          <label>Type de client</label>
          <div className="lf-seg" role="group" aria-label="Type de client">
            {typesClientPour(myRole).map((t) => {
              const on = (filtres.typeClient || "") === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={on ? "lf-on" : ""}
                  aria-pressed={on}
                  onClick={() => updateFilter("typeClient", t.id)}
                >
                  {t.icon && <i className={`fa ${t.icon}`} aria-hidden="true" />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lf-field">
          <label htmlFor="client-filtre-statut-compte">Statut du compte</label>
          <select
            id="client-filtre-statut-compte"
            className="lf-select"
            value={filtres.statutCompte}
            onChange={(e) => updateFilter("statutCompte", e.target.value === "" ? "" : Number(e.target.value))}
          >
            <option value="">Tous</option>
            {ACCOUNT_STATUS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <ChoixMultiple
          id="client-filtre-compagnies"
          label="Compagnies d'assurance"
          options={insuranceCompanies}
          valeurs={filtres.compagniesAssurances}
          onToggle={(id) => basculer("compagniesAssurances", id)}
          disabled={lieAuContrat("compagniesAssurances")}
          avecRecherche={insuranceCompanies.length > 8}
        />

        <ChoixMultiple
          id="client-filtre-types-produit"
          label="Types de produit"
          options={productTypes}
          valeurs={filtres.typesProduits}
          onToggle={(id) => basculer("typesProduits", id)}
          disabled={lieAuContrat("typesProduits")}
          avecRecherche={productTypes.length > 8}
        />

        <ChoixMultiple
          id="client-filtre-produits"
          label="Produits"
          options={produitsVisibles}
          valeurs={filtres.produits}
          onToggle={(id) => basculer("produits", id)}
          disabled={lieAuContrat("produits")}
          avecRecherche={produitsVisibles.length > 8}
          vide="Aucun produit pour ces types"
        />

        {myRole !== "PARTNER" && (
          <div className={"lf-field" + (lieAuContrat("partenaire") ? " lf-off" : "")}>
            <label htmlFor="client-filtre-partenaire">Société partenaire</label>
            <select
              id="client-filtre-partenaire"
              className="lf-select"
              value={filtres.partenaire}
              disabled={lieAuContrat("partenaire")}
              onChange={(e) => updateFilter("partenaire", e.target.value)}
            >
              <option value="">Toutes</option>
              {partenaires.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.company}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={"lf-field" + (lieAuContrat("auMoins1Contrat") ? " lf-off" : "")}>
          <label>Options</label>
          <div className="lf-toggles">
            <button
              type="button"
              className={"lf-toggle" + (filtres.auMoins1Contrat ? " lf-on" : "")}
              aria-pressed={filtres.auMoins1Contrat}
              disabled={lieAuContrat("auMoins1Contrat")}
              onClick={() => updateFilter("auMoins1Contrat", !filtres.auMoins1Contrat)}
            >
              <span className="lf-sw" aria-hidden="true" />
              Au moins 1 contrat
            </button>
            <button
              type="button"
              className={"lf-toggle" + (filtres.avecEcheancierOuMensualisation ? " lf-on" : "")}
              aria-pressed={filtres.avecEcheancierOuMensualisation}
              disabled={lieAuContrat("avecEcheancierOuMensualisation")}
              onClick={() =>
                updateFilter("avecEcheancierOuMensualisation", !filtres.avecEcheancierOuMensualisation)
              }
            >
              <span className="lf-sw" aria-hidden="true" />
              Avec échéancier ou mensualisation
            </button>
          </div>
        </div>
      </div>

      <div className="lf-panel-foot">
        <button type="button" className="lf-link" onClick={actions.clearAllFilters}>
          <i className="fa fa-refresh" aria-hidden="true" />
          Réinitialiser
        </button>
        <span className="lf-right">
          <button type="button" className="lf-btn lf-btn-ghost" onClick={onClose}>
            Fermer
          </button>
          <button type="submit" className="lf-btn lf-btn-primary" disabled={isLoading}>
            <i className={isLoading ? "fa fa-spinner fa-spin" : "fa fa-search"} aria-hidden="true" />
            Rechercher
          </button>
        </span>
      </div>
    </div>
  );
};

export default ClientAdvancedFiltersPanel;
